import axios from "axios";

const ATTR_KEY = "ascended_attribution";
const SESSION_KEY = "tb_session_id";
import { API } from "./api";

export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utms = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid",
     "keyword", "matchtype"].forEach((k) => {
      const v = params.get(k);
      if (v) utms[k] = v;
    });
    if (Object.keys(utms).length > 0) {
      const stored = getAttribution();
      const hasNewGclid = utms.gclid && utms.gclid !== stored.gclid;
      const isExpired = stored.captured_at && (new Date() - new Date(stored.captured_at)) > 90 * 24 * 60 * 60 * 1000;
      if (!stored.captured_at || hasNewGclid || isExpired) {
        localStorage.setItem(ATTR_KEY, JSON.stringify({ ...utms, referrer_url: document.referrer || "", landing_page: window.location.pathname, captured_at: new Date().toISOString() }));
      }
    }
  } catch (e) {}
}

export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTR_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function getSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

const VISITOR_KEY = "tb_visitor_id";

export function getVisitorId() {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid = `v_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(VISITOR_KEY, vid);
    }
    return vid;
  } catch (e) { return ""; }
}

let _pvStart = Date.now();
let _pvPath = window.location.pathname;
function _flushTime() {
  const secs = Math.round((Date.now() - _pvStart) / 1000);
  _pvStart = Date.now();
  return secs;
}
window.addEventListener("beforeunload", () => {
  try {
    const duration = _flushTime();
    if (duration >= 2 && !PRIVATE_PATH.test(_pvPath)) {
      axios.post(`${API}/events`, {
        event: "page_time", props: { path: _pvPath }, page: _pvPath,
        attribution: getAttribution(), session_id: getSessionId(),
        visitor_id: getVisitorId(), referrer: document.referrer || "", duration,
      }, { headers: { "x-beacon": "1" } }).catch(() => {});
    }
  } catch (e) {}
});

let TRACK = {};

const ADS_CONV_EVENTS = { signup: "signup", application_submit: "application", payment_success: "payment" };
const META_EVENTS = { signup: "CompleteRegistration", application_submit: "SubmitApplication", payment_success: "Purchase" };

function loadScript(src) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

function ensureGtag(id) {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
  window.gtag("js", new Date());
}

export function trackEvent(name, props = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...props });
  } catch (e) {}
  try {
    if (name === "page_view" && TRACK.ga4_id && window.gtag) {
      window.gtag("event", "page_view", { page_path: props.path || window.location.pathname });
    }
  } catch (e) {}
  try {
    const label = TRACK.google_ads_labels && TRACK.google_ads_labels[ADS_CONV_EVENTS[name]];
    if (label && TRACK.google_ads_id && window.gtag) {
      window.gtag("event", "conversion", { send_to: `${TRACK.google_ads_id}/${label}` });
    }
  } catch (e) {}
  try {
    if (META_EVENTS[name] && window.fbq) window.fbq("track", META_EVENTS[name]);
  } catch (e) {}
  try {
    let duration = 0;
    if (name === "page_view") {
      duration = _pvPath === (props.path || window.location.pathname) ? 0 : _flushTime();
      _pvPath = props.path || window.location.pathname;
    }
    axios.post(`${API}/events`, {
      event: name,
      props,
      page: window.location.pathname,
      attribution: getAttribution(),
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      referrer: document.referrer || "",
      duration,
    }).catch(() => {});
  } catch (e) {}
}

const PRIVATE_PATH = /^(\/console-[^/]*|\/qa)(\/|$)/;

export function hasSalesIQ() {
  return !!TRACK.salesiq_widget_code;
}

export async function initAnalytics() {
  if (PRIVATE_PATH.test(window.location.pathname)) return;
  try {
    const res = await axios.get(`${API}/integrations/public`, { timeout: 5000 });
    TRACK = res.data || {};
  } catch (e) {
    TRACK = {};
  }
  window.dispatchEvent(new Event("tb:track-ready"));
  const gtmId = TRACK.gtm_id || process.env.REACT_APP_GTM_ID;
  if (gtmId) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    String(gtmId).split(",").map((s) => s.trim()).filter(Boolean).forEach((id) => {
      loadScript(`https://www.googletagmanager.com/gtm.js?id=${id}`);
    });
  }
  if (TRACK.ga4_id) {
    ensureGtag(TRACK.ga4_id);
    window.gtag("config", TRACK.ga4_id);
  }
  if (TRACK.google_ads_id) {
    ensureGtag(TRACK.google_ads_id);
    window.gtag("config", TRACK.google_ads_id);
  }
  const clarityId = TRACK.clarity_id || process.env.REACT_APP_CLARITY_ID;
  if (clarityId) {
    window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    loadScript(`https://www.clarity.ms/tag/${clarityId}`);
  }
  if (TRACK.meta_pixel_id && !window.fbq) {
    const n = (window.fbq = function () { n.queue.push(arguments); });
    n.queue = [];
    n.loaded = true;
    n.version = "2.0";
    loadScript("https://connect.facebook.net/en_US/fbevents.js");
    window.fbq("init", TRACK.meta_pixel_id);
    window.fbq("track", "PageView");
  }
  if (TRACK.linkedin_partner_id) {
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(TRACK.linkedin_partner_id);
    loadScript("https://snap.licdn.com/li.lms-analytics/insight.min.js");
  }
  if (TRACK.salesiq_widget_code) {
    window.$zoho = window.$zoho || {};
    window.$zoho.salesiq = window.$zoho.salesiq || { ready: function () {} };
    if (window.matchMedia("(max-width: 767px)").matches) {
      const hideLauncher = () => { try { window.$zoho.salesiq.floatbutton.visible("hide"); } catch (e) {} };
      const prevReady = window.$zoho.salesiq.ready;
      window.$zoho.salesiq.ready = function () {
        if (typeof prevReady === "function") { try { prevReady(); } catch (e) {} }
        hideLauncher();
        setTimeout(hideLauncher, 1500);
        setTimeout(hideLauncher, 4000);
      };
    }
    const s = document.createElement("script");
    s.id = "zsiqscript";
    s.defer = true;
    s.src = `https://salesiq.zohopublic.com/widget?wc=${TRACK.salesiq_widget_code}`;
    document.body.appendChild(s);
  }
  if (TRACK.bing_verify_code && !document.querySelector('meta[name="msvalidate.01"]')) {
    const m = document.createElement("meta");
    m.name = "msvalidate.01";
    m.content = TRACK.bing_verify_code;
    document.head.appendChild(m);
  }
  if (TRACK.posthog_key) {
    // Named instance so it never collides with the platform's built-in PostHog.
    const ph = (window.posthog = window.posthog || []);
    const host = (TRACK.posthog_host || "https://us.i.posthog.com").replace(/\/$/, "");
    if (!ph.__tb_loaded) {
      ph.__tb_loaded = true;
      const s = document.createElement("script");
      s.async = true;
      s.src = host.replace(/(\.)i\.posthog\.com/, "$1assets.i.posthog.com") + "/static/array.js";
      document.head.appendChild(s);
      ph._i = ph._i || [];
      ph._i.push([TRACK.posthog_key, { api_host: host, autocapture: true, capture_pageview: true, capture_pageleave: true }, "tbapp"]);
    }
  }
  if (TRACK.custom_head_code && !window.__tbCustomHeadDone) {
    window.__tbCustomHeadDone = true;
    document.head.insertAdjacentHTML("beforeend", TRACK.custom_head_code);
  }
  if (TRACK.custom_body_code && !window.__tbCustomBodyDone) {
    window.__tbCustomBodyDone = true;
    document.body.insertAdjacentHTML("beforeend", TRACK.custom_body_code);
  }
}
